// ═══════════════════════════════════════════════════════════════════════════
// feedApi.js — the web app's client for the integration feed API (/api/feed/*)
// ═══════════════════════════════════════════════════════════════════════════
// The ONLY module that knows the feed API's paths and wire shapes. The
// contract is docs/integrations/feed-api-contract.md; where the .NET
// Netrev.Integrations host differs from it, this file follows the host, item
// by item, as docs/integrations/FEED_ALIGN_STATUS.md section 1 records.
// Components pass and receive camelCase plain objects; snake_case stays inside
// this file in both directions.
//
// • Calls go to API_BASE with the signed-in user's Supabase access token as a
//   bearer, the same way lib/api.js calls the backend.
// • Every failure rejects with a FeedApiError carrying { status, error }: the
//   HTTP status (0 when no HTTP answer was used) and a stable snake_case code
//   the screens translate. Server codes pass through unchanged, and the main
//   API's four authentication and platform strings ("No token", "Invalid
//   token", "Forbidden", "Bad Request") read as their contract codes; this
//   module adds not_signed_in, invalid_request, too_many_rows, network_error,
//   timeout, bad_response and unexpected_response (contract section 7).
// • Hidden unless the server says otherwise: getFeedAccess() and
//   isFeedEnabled() never reject. Any non-2xx answer, network error, timeout
//   or bad JSON reads as "feed off".
// • Privacy: nothing here logs, caches or stores anything. A connection token
//   is handed to the caller once and kept nowhere. Import rows are rebuilt
//   from an allow-list of report fields, so a customer column that slipped
//   past the parser is never uploaded.
// ═══════════════════════════════════════════════════════════════════════════
import { API_BASE } from "./api";
import { getCurrentSession } from "../data/auth";

export const FEED_PROVIDERS = Object.freeze(["urbanpiper", "grubtech", "deliverect"]);
// Rows per import request (contract 5.11). The upload screen chunks by this.
export const IMPORT_MAX_ROWS = 500;

const TIMEOUT_MS = 20000;
const ENABLED_TIMEOUT_MS = 8000;   // the visibility probe fails closed quickly
// Import chunks and admin rebuilds. Above the host's own 120 s import timeout,
// so its 504 timeout answers before this module gives up.
const LONG_TIMEOUT_MS = 130000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
// A server error code: never a sentence, so it can always be translated.
const CODE_RE = /^[a-z][a-z0-9_]{0,63}$/;
// The four non-code strings the main API's authentication and platform answer,
// read as the contract's codes (section 3). Express's requireAuth answers "No
// token" and "Invalid token" (401) on its own routes (it serves no /api/feed
// route, so it never answers them here); the .NET foundation's authentication
// copies both and adds "Forbidden" (403) and "Bad Request" (400)
// (SupabaseAuthenticationDefaults.cs, NetrevErrors.cs). The integrations host
// answers the contract's codes today and means to keep them (its status
// section 5); if it adopts the foundation's writers, these still read as
// codes. An exact, own-key lookup: any other sentence is still
// unexpected_response, and CODE_RE stays strict, so a sentence is never shown
// as a code.
const PLATFORM_ERROR_CODES = Object.freeze({
  "No token": "unauthorized",
  "Invalid token": "unauthorized",
  Forbidden: "forbidden",
  "Bad Request": "invalid_request",
});

export class FeedApiError extends Error {
  constructor(status, error) {
    super(error);
    this.name = "FeedApiError";
    this.status = status;
    this.error = error;
  }
}

// Refused before anything is sent: status 0.
const refuse = (error = "invalid_request") => new FeedApiError(0, error);

// ── argument checks ─────────────────────────────────────────────────────────
const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

function uuid(v) {
  if (typeof v !== "string" || !UUID_RE.test(v)) throw refuse();
  return v;
}
function uuidOrNull(v) {
  return v == null ? null : uuid(v);
}
function day(v) {
  if (typeof v !== "string" || !DATE_RE.test(v)) throw refuse();
  return v;
}
function dayOrNull(v) {
  return v == null || v === "" ? null : day(v);
}
function text(v) {
  if (typeof v !== "string") throw refuse();
  return v.trim();
}
function record(v, ...required) {
  if (!isObject(v) || required.some((k) => !has(v, k))) throw refuse();
  return v;
}
function nonEmptyList(v) {
  if (!Array.isArray(v) || v.length === 0) throw refuse();
  return v;
}

// ── one HTTP exchange ───────────────────────────────────────────────────────
async function bearer() {
  try {
    const session = await getCurrentSession();
    const token = session && session.access_token;
    return typeof token === "string" && token ? token : null;
  } catch (_) {
    return null;
  }
}

function withQuery(path, query) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query || {})) {
    if (v != null && v !== "") q.append(k, String(v));
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

// Resolves { status, data } for a 2xx answer whose body is a JSON object;
// rejects with a FeedApiError for everything else.
async function call(method, path, { query, body, timeoutMs = TIMEOUT_MS } = {}) {
  const token = await bearer();
  if (!token) throw refuse("not_signed_in");

  const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };
  const init = { method, headers, cache: "no-store" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  if (controller) init.signal = controller.signal;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let status = 0;
  let raw = "";
  try {
    const res = await fetch(`${API_BASE}/api/feed${withQuery(path, query)}`, init);
    status = typeof res.status === "number" ? res.status : 0;
    raw = await res.text();
  } catch (_) {
    throw refuse(controller && controller.signal.aborted ? "timeout" : "network_error");
  } finally {
    if (timer) clearTimeout(timer);
  }

  let data = null;
  let parsed = false;
  try {
    data = JSON.parse(raw);
    parsed = true;
  } catch (_) {
    /* not JSON: an nginx error page, an SPA fallback, an empty body */
  }
  if (!(status >= 200 && status < 300)) {
    const error = parsed && isObject(data) && typeof data.error === "string" ? data.error : null;
    let code = "unexpected_response";
    if (error != null && has(PLATFORM_ERROR_CODES, error)) code = PLATFORM_ERROR_CODES[error];
    else if (error != null && CODE_RE.test(error)) code = error;
    throw new FeedApiError(status, code);
  }
  if (!parsed || !isObject(data)) throw new FeedApiError(status, "bad_response");
  return { status, data };
}

// ── response normalisers (wire snake_case → camelCase plain objects) ────────
const str = (v) => (typeof v === "string" ? v : null);
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const countOrNull = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);
const count = (v) => countOrNull(v) ?? 0;
const strings = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
const each = (v, norm) => (Array.isArray(v) ? v.map(norm).filter(Boolean) : []);

// A list or object the contract requires in a 2xx body; missing = bad response.
function listIn(res, key, norm) {
  if (!Array.isArray(res.data[key])) throw new FeedApiError(res.status, "bad_response");
  return each(res.data[key], norm);
}
function objectIn(res, key, norm) {
  const out = norm(res.data[key]);
  if (!out) throw new FeedApiError(res.status, "bad_response");
  return out;
}

function normConnection(c) {
  if (!isObject(c) || typeof c.id !== "string") return null;
  return {
    id: c.id,
    accountId: str(c.account_id),
    accountName: str(c.account_name),
    provider: str(c.provider),
    providerAccountRef: str(c.provider_account_ref),
    status: str(c.status),
    activationShape: str(c.activation_shape),
    approved: c.approved === true,
    approvedAt: str(c.approved_at),
    consentedAt: str(c.consented_at),
    consentTermsVersion: str(c.consent_terms_version),
    pausedAt: str(c.paused_at),
    pauseReason: str(c.pause_reason),
    disconnectedAt: str(c.disconnected_at),
    firstEventAt: str(c.first_event_at),
    lastEventAt: str(c.last_event_at),
    createdAt: str(c.created_at),
    updatedAt: str(c.updated_at),
    storesTotal: count(c.stores_total),
    storesUnmapped: count(c.stores_unmapped),
    storesLiveMonth: countOrNull(c.stores_live_month),   // admin list only
    ordersToday: count(c.orders_today),
    warnings: strings(c.warnings),
  };
}

function normCredential(t) {
  if (!isObject(t) || typeof t.id !== "string") return null;
  return { id: t.id, createdAt: str(t.created_at), firstSeenAt: str(t.first_seen_at), revokedAt: str(t.revoked_at) };
}

function normTodayRow(r) {
  if (!isObject(r)) return null;
  return {
    workspaceId: str(r.workspace_id),
    channelId: str(r.channel_id),
    platformCode: str(r.platform_code),
    localDate: str(r.local_date),
    orders: count(r.orders),
    gross: num(r.gross),
  };
}

// One channel-day with fully discounted fed orders (host difference 30). No
// location id comes with it: the screen finds the channel among the account's
// locations.
function normZeroNetDay(d) {
  if (!isObject(d) || typeof d.channel_id !== "string" || !d.channel_id || typeof d.log_date !== "string" || !d.log_date) return null;
  return { channelId: d.channel_id, logDate: d.log_date, orders: count(d.orders) };
}

function normConnectionDetail(c) {
  const base = normConnection(c);
  if (!base) return null;
  return {
    ...base,
    tokens: each(c.tokens, normCredential),
    todayByPlatform: each(c.today_by_platform, normTodayRow),
    openOrders: count(c.open_orders),
    unprocessedEvents: count(c.unprocessed_events),
    // Orders on fed days discounted by 100% or more (order_subtotal − discount
    // ≤ 0): shown as a warning, never changed (contract 4.3). Missing reads as
    // 0, i.e. nothing to warn about.
    fullyDiscountedOrders: count(c.fully_discounted_orders),
    // The latest channel-days that hold them, newest first (host difference
    // 30): [{ channelId, logDate, orders }]. Missing reads as [].
    fullyDiscountedDays: each(c.fully_discounted_days, normZeroNetDay),
  };
}

function normStorePlatform(p) {
  if (!isObject(p) || typeof p.platform_code !== "string") return null;
  return {
    platformCode: p.platform_code,
    channelId: str(p.channel_id),
    ordersSeen: count(p.orders_seen),
    ordersHeld: count(p.orders_held),
    lastOrderAt: str(p.last_order_at),
  };
}

function normStore(s) {
  if (!isObject(s) || typeof s.id !== "string") return null;
  return {
    id: s.id,
    providerStoreId: str(s.provider_store_id) ?? "",
    providerBrandId: str(s.provider_brand_id) ?? "",
    posStoreRef: str(s.pos_store_ref),
    storeName: str(s.store_name),
    brandName: str(s.brand_name),
    workspaceId: str(s.workspace_id),
    mappedAt: str(s.mapped_at),
    firstSeenAt: str(s.first_seen_at),
    lastSeenAt: str(s.last_seen_at),
    ordersHeld: count(s.orders_held),
    platforms: each(s.platforms, normStorePlatform),
  };
}

function normChannel(ch) {
  if (!isObject(ch) || typeof ch.id !== "string") return null;
  const m = isObject(ch.channel_map) ? ch.channel_map : null;
  return {
    id: ch.id,
    platform: str(ch.platform),
    isActive: ch.is_active !== false,
    channelMap: m
      ? { connectionId: str(m.connection_id), platformCode: str(m.platform_code), feedFrom: str(m.feed_from), feedUntil: str(m.feed_until) }
      : null,
  };
}

function normWorkspace(w) {
  if (!isObject(w) || typeof w.id !== "string") return null;
  return {
    id: w.id,
    name: str(w.name),
    market: str(w.market),
    currency: str(w.currency),
    timeZone: str(w.time_zone),
    tradingDayCutoff: str(w.trading_day_cutoff),
    archivedAt: str(w.archived_at),
    channels: each(w.channels, normChannel),
  };
}

function normConflict(c) {
  if (!isObject(c) || typeof c.channel_id !== "string" || typeof c.log_date !== "string") return null;
  const typed = isObject(c.typed) ? c.typed : {};
  const feed = isObject(c.feed) ? c.feed : {};
  return {
    workspaceId: str(c.workspace_id),
    channelId: c.channel_id,
    platformCode: str(c.platform_code),
    logDate: c.log_date,
    typed: {
      rows: count(typed.rows),
      sources: strings(typed.sources),
      gross: num(typed.gross),
      netSales: num(typed.net_sales),
      orders: countOrNull(typed.orders),
    },
    // netSales is the raw net. netSalesCounted is the net as the Daily Log reads
    // the rows (net when above 0, else gross), comparable with typed.netSales;
    // null when the host doesn't send it (host difference 31).
    feed: {
      orders: count(feed.orders),
      gross: num(feed.gross),
      netSales: num(feed.net_sales),
      fullyDiscountedOrders: count(feed.fully_discounted_orders),
      netSalesCounted: num(feed.net_sales_counted),
    },
    // Replacing is the default: anything but an explicit keep reads as replace.
    choice: c.choice === "keep" ? "keep" : "replace",
    decidedAt: str(c.decided_at),
  };
}

function normDayChoice(c) {
  if (!isObject(c) || typeof c.channel_id !== "string") return null;
  return {
    channelId: c.channel_id,
    logDate: str(c.log_date),
    choice: c.choice === "keep" || c.choice === "replace" ? c.choice : null,
    decidedAt: str(c.decided_at),
    replacedCount: count(c.replaced_count),
  };
}

function normRejected(r) {
  if (!isObject(r) || !Number.isInteger(r.index) || r.index < 0) return null;
  return { index: r.index, reason: str(r.reason) ?? "unknown" };
}

function normLiveStore(s) {
  if (!isObject(s) || typeof s.connection_id !== "string") return null;
  return {
    connectionId: s.connection_id,
    accountId: str(s.account_id),
    accountName: str(s.account_name),
    provider: str(s.provider),
    providerAccountRef: str(s.provider_account_ref),
    providerStoreId: str(s.provider_store_id) ?? "",
    providerBrandId: str(s.provider_brand_id) ?? "",
    posStoreRef: str(s.pos_store_ref),
    storeName: str(s.store_name),
    brandName: str(s.brand_name),
    workspaceId: str(s.workspace_id),
    orders: count(s.orders),
    firstOrderAt: str(s.first_order_at),
    lastOrderAt: str(s.last_order_at),
  };
}

// create and rotate: the one-time token. Without one the answer is useless.
function issuedToken(res) {
  const d = res.data;
  if (typeof d.token !== "string" || d.token === "") throw new FeedApiError(res.status, "bad_response");
  return { token: d.token, headerName: str(d.header_name) ?? "X-Netrev-Token", webhookUrl: str(d.webhook_url) };
}

// ── import rows: allow-list of report fields (contract 5.11) ───────────────
// Only these fields are copied. Any other key on a row (a customer name,
// phone, address, city, customer id or payment transaction id) is dropped.
const IMPORT_TEXT_FIELDS = [
  ["providerOrderId", "provider_order_id"],
  ["orderCreatedAt", "order_created_at"],
  ["state", "state"],
  ["providerStoreId", "provider_store_id"],
  ["posStoreRef", "pos_store_ref"],
  ["storeName", "store_name"],
  ["brandName", "brand_name"],
  ["paymentMode", "payment_mode"],
  ["orderType", "order_type"],
  ["deliveryType", "delivery_type"],
];
const IMPORT_MONEY_FIELDS = [
  ["orderSubtotal", "order_subtotal"],
  ["discount", "discount"],
  ["aggregatorDiscount", "aggregator_discount"],
  ["merchantDiscount", "merchant_discount"],
  ["totalCharges", "total_charges"],
  ["totalTaxes", "total_taxes"],
  ["orderTotal", "order_total"],
];
const IMPORT_REF_FIELDS = [
  ["orderRefId", "order_ref_id"],
  ["externalPlatformId", "external_platform_id"],
];

// Ids can come out of a spreadsheet as numbers; the API stores ids as text.
function cellText(v) {
  if (v == null) return null;
  if (typeof v === "string") {
    const s = v.trim();
    return s === "" ? null : s;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  throw refuse();
}
// Money must already be a number: a string is never guessed at here.
function cellMoney(v) {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  throw refuse();
}

function importRow(r) {
  if (!isObject(r)) throw refuse();
  const row = {};
  for (const [from, to] of IMPORT_TEXT_FIELDS) row[to] = cellText(r[from]);
  const platform = cellText(r.platformCode);
  row.platform_code = platform ? platform.toLowerCase() : null;
  row.provider_brand_id = cellText(r.providerBrandId) ?? "";
  for (const [from, to] of IMPORT_MONEY_FIELDS) row[to] = cellMoney(r[from]);
  const refs = {};
  for (const [from, to] of IMPORT_REF_FIELDS) {
    const v = cellText(r[from]);
    if (v != null) refs[to] = v;
  }
  row.ext_order_refs = refs;
  return row;
}

// ═══════════════════════════════════════════════════════════════════════════
// Visibility (contract 5.1). Never rejects.
// ═══════════════════════════════════════════════════════════════════════════
function closedAccess() {
  return {
    enabled: false,
    superAdmin: false,
    accountId: null,
    providers: Object.fromEntries(FEED_PROVIDERS.map((p) => [p, false])),
  };
}

// { enabled, superAdmin, accountId, providers: { urbanpiper, grubtech, deliverect } }
export async function getFeedAccess({ accountId } = {}) {
  if (accountId != null && (typeof accountId !== "string" || !UUID_RE.test(accountId))) return closedAccess();
  let res;
  try {
    res = await call("GET", "/enabled", { query: { account_id: accountId }, timeoutMs: ENABLED_TIMEOUT_MS });
  } catch (_) {
    return closedAccess();
  }
  const d = res.data;
  if (d.enabled !== true) return closedAccess();
  const p = isObject(d.providers) ? d.providers : {};
  return {
    enabled: true,
    superAdmin: d.super_admin === true,
    accountId: str(d.account_id),
    providers: Object.fromEntries(FEED_PROVIDERS.map((k) => [k, p[k] === true])),
  };
}

// true only after a 2xx JSON answer with enabled === true (and, when a
// provider is named, that provider === true). false in every other case.
export async function isFeedEnabled({ accountId, provider } = {}) {
  const access = await getFeedAccess({ accountId });
  if (!access.enabled) return false;
  if (provider == null) return true;
  return FEED_PROVIDERS.includes(provider) && access.providers[provider] === true;
}

// ═══════════════════════════════════════════════════════════════════════════
// Connections (contract 5.2 to 5.5, 5.12, 5.13)
// ═══════════════════════════════════════════════════════════════════════════
export async function listConnections({ accountId } = {}) {
  const res = await call("GET", "/connections", { query: { account_id: uuid(accountId) } });
  return listIn(res, "connections", normConnection);
}

// Resolves { connection, token, headerName, webhookUrl }. The token is shown
// once: keep it only in the dialog that made this call.
export async function createConnection({ accountId, provider = "urbanpiper", providerAccountRef, consentTermsVersion } = {}) {
  const body = {
    account_id: uuid(accountId),
    provider: text(provider),
    provider_account_ref: text(providerAccountRef),
    consent_terms_version: text(consentTermsVersion),
  };
  const res = await call("POST", "/connections", { body });
  return { connection: normConnection(res.data.connection), ...issuedToken(res) };
}

export async function getConnection(connectionId) {
  const res = await call("GET", `/connections/${uuid(connectionId)}`);
  return objectIn(res, "connection", normConnectionDetail);
}

// Resolves { token, headerName, webhookUrl, credential }. Shown once, as above.
// Without options the request has no body (contract 5.5): the token in use
// keeps working until the new one's first event. { revokePrevious: true }
// sends { revoke_previous: true } (host difference 27; super admin only, others
// get 403 forbidden): every live token is revoked at once, for a token that
// leaked. false is never sent; it is the same as no body.
export async function rotateToken(connectionId, options) {
  const id = uuid(connectionId);
  if (options != null && !isObject(options)) throw refuse();
  const revokePrevious = options == null ? undefined : options.revokePrevious;
  if (revokePrevious != null && typeof revokePrevious !== "boolean") throw refuse();
  const request = revokePrevious === true ? { body: { revoke_previous: true } } : {};
  const res = await call("POST", `/connections/${id}/token/rotate`, request);
  return { ...issuedToken(res), credential: normCredential(res.data.credential) };
}

export async function pauseConnection(connectionId, { reason } = {}) {
  const id = uuid(connectionId);
  const body = reason == null || reason === "" ? {} : { reason: text(reason) };
  const res = await call("POST", `/connections/${id}/pause`, { body });
  return objectIn(res, "connection", normConnection);
}

// Resolves { connection, replacedDays }: replacedDays counts the typed days
// the resume replaced (their rows are kept for restore, contract 5.13).
export async function resumeConnection(connectionId) {
  const res = await call("POST", `/connections/${uuid(connectionId)}/resume`);
  return { connection: objectIn(res, "connection", normConnection), replacedDays: count(res.data.replaced_days) };
}

// ═══════════════════════════════════════════════════════════════════════════
// Stores and mapping (contract 5.6 to 5.8)
// ═══════════════════════════════════════════════════════════════════════════
export async function listStores(connectionId) {
  const res = await call("GET", `/connections/${uuid(connectionId)}/stores`);
  return listIn(res, "stores", normStore);
}

// The account's locations with their time zone, cutoff and channels (each
// with its current channel map). Resolves { accountId, workspaces }.
export async function getMappingTargets(connectionId) {
  const res = await call("GET", `/connections/${uuid(connectionId)}/mappings`);
  return { accountId: str(res.data.account_id), workspaces: listIn(res, "workspaces", normWorkspace) };
}

// Every item must name each of its fields explicitly, so a forgotten field
// can never unmap a store, clear a cutoff or switch a feed off by accident:
//   stores:      [{ id, workspaceId }]                          workspaceId null = unmap
//   workspaces:  [{ id, timeZone, tradingDayCutoff }]           cutoff null/"" = midnight
//   channelMaps: [{ workspaceId, platformCode, channelId, feedFrom }]  channelId null = remove,
//                                                                      feedFrom null/"" = not live
// Resolves { stores, workspaces, replacedDays }: replacedDays counts the typed
// days this save replaced (their rows are kept for restore, contract 5.8).
export async function saveMappings(connectionId, { stores, workspaces, channelMaps } = {}) {
  const id = uuid(connectionId);
  const body = {};
  if (stores != null) {
    if (!Array.isArray(stores)) throw refuse();
    body.stores = stores.map((s) => {
      record(s, "id", "workspaceId");
      return { id: uuid(s.id), workspace_id: uuidOrNull(s.workspaceId) };
    });
  }
  if (workspaces != null) {
    if (!Array.isArray(workspaces)) throw refuse();
    body.workspaces = workspaces.map((w) => {
      record(w, "id", "timeZone", "tradingDayCutoff");
      const cutoff = w.tradingDayCutoff == null || w.tradingDayCutoff === "" ? null : text(w.tradingDayCutoff);
      return { id: uuid(w.id), time_zone: text(w.timeZone), trading_day_cutoff: cutoff };
    });
  }
  if (channelMaps != null) {
    if (!Array.isArray(channelMaps)) throw refuse();
    body.channel_maps = channelMaps.map((m) => {
      record(m, "workspaceId", "platformCode", "channelId", "feedFrom");
      return {
        workspace_id: uuid(m.workspaceId),
        platform_code: text(m.platformCode).toLowerCase(),
        channel_id: uuidOrNull(m.channelId),
        feed_from: dayOrNull(m.feedFrom),
      };
    });
  }
  const items = (body.stores || []).length + (body.workspaces || []).length + (body.channel_maps || []).length;
  if (items === 0) throw refuse();
  const res = await call("PUT", `/connections/${id}/mappings`, { body });
  return {
    stores: listIn(res, "stores", normStore),
    workspaces: listIn(res, "workspaces", normWorkspace),
    replacedDays: count(res.data.replaced_days),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Typed days the feed replaced, and the super admin's keep override
// (contract 4.6, 5.9, 5.10). Replacing is the default and the server records
// it itself; a choice of null on a listed day reads as "replace".
// ═══════════════════════════════════════════════════════════════════════════
// Resolves { from, to, conflicts }. from/to are optional local dates.
export async function listConflicts(connectionId, { from, to } = {}) {
  const id = uuid(connectionId);
  const res = await call("GET", `/connections/${id}/conflicts`, { query: { from: dayOrNull(from), to: dayOrNull(to) } });
  return { from: str(res.data.from), to: str(res.data.to), conflicts: listIn(res, "conflicts", normConflict) };
}

// choices: [{ channelId, logDate, choice: "keep" | "replace" }]
// "keep" is the super admin override (the server refuses it from anyone
// else); "replace" undoes it. The same channel-day twice is refused before
// sending, as the host would refuse it (host difference 12). A replace that
// undoes a keep moves the typed rows out again only when the day has a
// counting order; otherwise its replacedCount is 0 and they stay, locked
// (host difference 32).
export async function saveDayChoices(connectionId, choices) {
  const id = uuid(connectionId);
  const seen = new Set();
  const body = {
    choices: nonEmptyList(choices).map((c) => {
      record(c, "channelId", "logDate", "choice");
      if (c.choice !== "keep" && c.choice !== "replace") throw refuse();
      const item = { channel_id: uuid(c.channelId), log_date: day(c.logDate), choice: c.choice };
      const key = `${item.channel_id.toLowerCase()}\n${item.log_date}`;
      if (seen.has(key)) throw refuse();
      seen.add(key);
      return item;
    }),
  };
  const res = await call("POST", `/connections/${id}/day-choices`, { body });
  return listIn(res, "choices", normDayChoice);
}

// ═══════════════════════════════════════════════════════════════════════════
// History import (contract 5.11): ONE request of at most IMPORT_MAX_ROWS rows.
// ═══════════════════════════════════════════════════════════════════════════
// rows: [{ providerOrderId, orderCreatedAt, state, platformCode, providerStoreId,
//          providerBrandId, posStoreRef, storeName, brandName, orderSubtotal,
//          discount, aggregatorDiscount, merchantDiscount, totalCharges,
//          totalTaxes, orderTotal, paymentMode, orderType, deliveryType,
//          orderRefId, externalPlatformId }]
// Money fields must be numbers or null. timeZone (IANA) is required by the
// server when an orderCreatedAt has no offset.
// Resolves { received, inserted, updated, unchanged, rejected, storesNew,
//            daysRecomputed, replacedDays, warnings }.
// The host applies an import asynchronously (host difference 2): the rows are
// stored in its inbox and the feed applies them later, like live events,
// while the connection is approved and not paused. So inserted counts rows
// newly stored (a corrected row counts as new, host difference 24), unchanged
// the rows it already held, and updated, daysRecomputed and replacedDays are
// 0 for this JSON upload: days are rolled up, and typed rows replaced, when
// the stored rows apply. received = inserted + updated + unchanged + rejected.
// warnings (host difference 18) are the host's notes for the person
// uploading, as English sentences carrying no customer values: counts,
// dates, zone ids, platform codes and day totals (a file upload, which this
// client doesn't use, can add dropped header names). The host's code bounds
// them; this client doesn't check their content. Non-blank strings only, []
// when absent. Show them as text; never pass them through t().
export async function importOrderChunk(connectionId, { timeZone, rows } = {}) {
  const id = uuid(connectionId);
  nonEmptyList(rows);
  if (rows.length > IMPORT_MAX_ROWS) throw refuse("too_many_rows");
  const body = { rows: rows.map(importRow) };
  if (timeZone != null && timeZone !== "") body.time_zone = text(timeZone);
  const res = await call("POST", `/connections/${id}/import`, { body, timeoutMs: LONG_TIMEOUT_MS });
  const d = res.data;
  return {
    received: count(d.received),
    inserted: count(d.inserted),
    updated: count(d.updated),
    unchanged: count(d.unchanged),
    rejected: each(d.rejected, normRejected),
    storesNew: count(d.stores_new),
    daysRecomputed: count(d.days_recomputed),
    replacedDays: count(d.replaced_days),
    warnings: strings(d.warnings).filter((w) => w.trim() !== ""),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Super admin (contract 5.14 to 5.17)
// ═══════════════════════════════════════════════════════════════════════════
export async function adminListConnections({ accountId } = {}) {
  const res = await call("GET", "/admin/connections", { query: { account_id: uuidOrNull(accountId) } });
  return listIn(res, "connections", normConnection);
}

export async function adminSetApproval(connectionId, approved) {
  const id = uuid(connectionId);
  if (typeof approved !== "boolean") throw refuse();
  const res = await call("POST", `/admin/connections/${id}/approve`, { body: { approved } });
  return objectIn(res, "connection", normConnection);
}

// Resolves { daysRecomputed, rowsWritten, rowsDeleted }.
export async function adminRebuild(connectionId, { from, to } = {}) {
  const id = uuid(connectionId);
  const res = await call("POST", `/admin/connections/${id}/rebuild`, {
    query: { from: day(from), to: day(to) },
    timeoutMs: LONG_TIMEOUT_MS,
  });
  const d = res.data;
  return { daysRecomputed: count(d.days_recomputed), rowsWritten: count(d.rows_written), rowsDeleted: count(d.rows_deleted) };
}

// month: "YYYY-MM". Resolves { month, stores }.
export async function adminLiveStores(month) {
  if (typeof month !== "string" || !MONTH_RE.test(month)) throw refuse();
  const res = await call("GET", "/admin/live-stores", { query: { month } });
  return { month: str(res.data.month), stores: listIn(res, "stores", normLiveStore) };
}
